<template>
  <div class="segmentation-widget">
    <div class="ring-chart-container">
      <v-chart :option="ringOption" autoresize class="ring-chart" />

      <div class="center-text" :style="{ color: zoneColor }">
        <div class="avg-duration" style="color: #ccc">{{ timeSpan}} </div>
        <p class="total">{{ formattedTotal  }}</p>
        <p class="label" :style="{ color: zoneColor }">{{ $t('charts.segmentation.visitors') }}</p>
        <p class="avg-duration" :style="{ color: zoneColor }">{{ $t('charts.segmentation.avg', { n: avgText(results.total_average_duration) }) }}</p>
      </div>
    </div>

    <div v-if="results.visitors_duration_segmentation.strategy != 'insufficient_data'" class="segments">
      <div class="segment" v-for="segment in segments" :key="segment.name">
        <i :class="segment.iconClass" class="icon" :style="{ color: segment.color }"></i>
        <div class="label" :style="{ color: segment.color }">{{ segment.name }}</div>
        <div class="value" :style="{ color: segment.color }">{{ $t('charts.segmentation.visitorCount', { n: countText(segment.count) }) }}</div>
        <div class="value" :style="{ color: segment.color }">{{ $t('charts.segmentation.avg', { n: avgText(segment.duration) }) }}</div>
        <div class="value" :style="{ color: segment.color }" style="padding-top: 10px; padding-bottom: 10px;"><i class="bi bi-arrow-down-square-fill"></i></div>
        <div class="total" :style="{ color: segment.color }">{{ pctText(segment.percentage) }}</div>
        <div class="value" :style="{ color: segment.color }">{{ $t('charts.segmentation.range', { min: numText(segment.minvalue), max: numText(segment.maxvalue) }) }}</div>
      </div>
    </div>

    <!-- Fallback message for insufficient data -->
    <div v-else style="margin-top: 100px; text-align: center;">
        <p style="color: #425461; font-style: italic;">{{ $t('charts.segmentation.insufficientData') }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { PieChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatPercent, formatDuration } from '@/i18n/format'

// Register them with ECharts
use([PieChart, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  results: Object,
  durations: Object,
  zoneColor: String,
  timeSpan: String,
  detectionZoneTypeName: String
})

const palette = computed(() => {
  return detection_zone_palettes[ props.detectionZoneTypeName ] ?? {
    segmentation: {
      walkBy:      '#B2C149',
      shortLinger: '#4F7EB3',
      clearLinger: '#425461'
    }
  }
})  

const { t } = useI18n()

// English shows the values exactly as before; Dutch goes through format.js
// (1.234 · 12,34% · "Gem. 1 min 10 sec" instead of raw seconds).
const isEn = () => currentLanguage() === 'en'
const decimalsOf = v => Math.min(2, (String(v).split('.')[1] || '').length)
const numText = v => (isEn() || v === null || v === undefined || v === '') ? v : formatNumber(v, { decimals: decimalsOf(v) })
const countText = n => isEn() ? n.toLocaleString() : formatNumber(n)
const avgText = v => isEn() ? v : formatDuration(v)
const pctText = p => isEn() ? `${p}%` : formatPercent(p, { decimals: 2 })

const formattedTotal = computed(() => {
  const total = props.results.total_visitors ?? 0
  return isEn() ? total.toLocaleString() : formatNumber(total)
})

const segments = computed(() => [
  {
    name: t('charts.segments.walkBys'),
    count: props.results.visitors_duration_segmentation.amount_walk_bys,
    duration: props.results.average_duration_walk_bys,
    percentage: ((props.results.visitors_duration_segmentation.amount_walk_bys/props.results.total_visitors)*100).toFixed(2),
    minvalue: '0',
    maxvalue: props.durations.walkby_cutoff,
    color: palette.value.segmentation.walkBy,
    iconClass: 'bi bi-person-walking'
  },
  {
    name: t('charts.segments.shortLingerers'),
    count: props.results.visitors_duration_segmentation.amount_short_lingerers,
    duration: props.results.visitors_duration_segmentation.avg_short_duration,
    percentage: ((props.results.visitors_duration_segmentation.amount_short_lingerers/props.results.total_visitors)*100).toFixed(2),
    minvalue: props.durations.walkby_cutoff,
    maxvalue: props.durations.short_lingerer_cutoff,
    color: palette.value.segmentation.shortLinger,
    iconClass: 'bi bi-person-standing'
  },
  {
    name: t('charts.segments.clearLingerers'),
    count: props.results.visitors_duration_segmentation.amount_clear_lingerers,
    duration: props.results.visitors_duration_segmentation.avg_clear_duration,
    percentage: ((props.results.visitors_duration_segmentation.amount_clear_lingerers/props.results.total_visitors)*100).toFixed(2),
    minvalue: props.durations.short_lingerer_cutoff,
    maxvalue: Math.ceil(props.durations.max_duration),
    color: palette.value.segmentation.clearLinger,
    iconClass: 'bi bi-person-raised-hand'
  }
])

const ringOption = computed(() => ({
  series: [
    {
      type: 'pie',
      radius: ['70%', '90%'],
      avoidLabelOverlap: false,
      label: { show: false },
      labelLine: { show: false },
      data: segments.value.map(segment => ({
        value: segment.count,
        name: segment.name,
        itemStyle: { color: segment.color }
      }))
    }
  ]
}))

</script>

<style scoped>
.segmentation-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ring-chart-container {
  position: relative;
  width: 200px;
  height: 200px;
}

.ring-chart {
  width: 100%;
  height: 100%;
}

.center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.center-text p {
  margin: 2px 0;
}

.center-text .total {
  font-size: 24px;
  font-weight: bold;
}

.center-text .label {
  color: #888;
}

.center-text .avg-duration {
  font-size: 12px;
  margin-top: 4px;
}

.segments {
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-top: 20px;
  flex-wrap: wrap;
}

.segment {
  text-align: center;
  margin: 0 10px;
}

.segment .icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.segment .value {
  font-size: 14px;
  color: #555;
}
</style>